import React, {useId, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ref as storageRef, uploadBytesResumable, getDownloadURL} from 'firebase/storage'
import {storage} from '../../firebase.ts'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import IconClose from '../../assets/icons/close.svg?react'
import IconDownloadArrow from '../../assets/icons/download-arrow.svg?react'

interface ItineraryAttachmentFieldProps {
    value?: string
    // Section heading. Callers that follow the "Asignar código" design pass the
    // 13px semibold heading + "opcional"; the rest get the plain small label.
    label?: React.ReactNode
    onChange: (url: string | undefined) => void
    // Lets the caller disable its Save button mid-upload, same as
    // TripBookingForm's isFormEnabled/isUploadingPdf gate.
    onUploadingChange?: (isUploading: boolean) => void
}

// Same upload-to-Firebase-Storage flow as TripBookingForm/CreateUpdateTripBookDialog
// (tripItineraryPdfUrl is the same backend field either writes), plus a second way
// in: pasting a link the agency already has, for when the itinerary lives somewhere
// else and re-uploading it would be pointless. Give the caller a `key` tied to the
// record id so a record switch remounts this and drops any local upload/paste state.
export const ItineraryAttachmentField: React.FC<ItineraryAttachmentFieldProps> = ({value, label, onChange, onUploadingChange}) => {
    const {t} = useTranslation('dashboard')
    const fileInputId = useId()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [fileName, setFileName] = useState('')
    const [urlDraft, setUrlDraft] = useState('')

    const setUploading = (uploading: boolean) => {
        setIsUploading(uploading)
        onUploadingChange?.(uploading)
    }

    const upload = (file: File) => {
        setUploading(true)
        setFileName(file.name)
        const path = `tripItineraries/${Date.now()}_${file.name}`
        const uploadTask = uploadBytesResumable(storageRef(storage, path), file)
        uploadTask.on('state_changed', null,
            (error) => {
                console.error(error)
                setUploading(false)
            },
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref)
                onChange(url)
                setUploading(false)
            }
        )
    }

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) upload(file)
    }

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault()
        setIsDragging(false)
        const file = e.dataTransfer.files?.[0]
        if (file?.type === 'application/pdf') upload(file)
    }

    // The link is committed on blur/Enter rather than on every keystroke, so a
    // half-typed URL never lands in the record.
    const commitUrl = () => {
        const trimmed = urlDraft.trim()
        if (!trimmed) return
        onChange(trimmed)
        setUrlDraft('')
    }

    const clear = () => {
        onChange(undefined)
        setFileName('')
        setUrlDraft('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    return (
        <div className="flex flex-col gap-[7px]">
            {label ?? <span className="text-xs text-neutralBlack-500">{t('itineraryPdf')}</span>}
            <input ref={fileInputRef} id={fileInputId} type="file" accept=".pdf,application/pdf" className="hidden" onChange={handleFileSelected}/>

            {isUploading ? (
                <span className="inline-flex self-start items-center gap-2.5 h-[38px] pl-3.5 pr-4 rounded-full border border-neutralBlack-200 text-[13px] font-medium text-neutralBlack-500">
                    <IconSpinner className="size-4 flex-none"/>
                    <span className="truncate">{t('uploadingPdf')}</span>
                </span>
            ) : value ? (
                <div className="inline-flex self-start items-center gap-2.5 max-w-full py-2 pl-3.5 pr-2.5 rounded-full border border-neutralBlack-200">
                    <span className="flex-none w-1.5 h-1.5 rounded-full bg-[#12734A]"/>
                    <a href={value} target="_blank" rel="noreferrer"
                       className="min-w-0 truncate text-[13px] font-medium text-neutralBlack-900 hover:underline">
                        {fileName || value}
                    </a>
                    <button type="button" onClick={clear} title={t('remove')} aria-label={t('remove')}
                            className="flex-none w-[22px] h-[22px] flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-600 hover:bg-neutralBlack-200 transition-colors duration-200 ease-out">
                        <IconClose className="size-2"/>
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2.5 flex-wrap">
                    <label
                        htmlFor={fileInputId}
                        onDragOver={(e) => {e.preventDefault(); if (!isDragging) setIsDragging(true)}}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                        className={`inline-flex items-center gap-2 h-[38px] px-4 rounded-full border border-dashed text-[13px] font-medium text-neutralBlack-700 cursor-pointer transition-colors duration-200 ease-out ${isDragging ? 'border-neutralBlack-900 bg-neutralBlack-50' : 'border-neutralBlack-300 hover:border-neutralBlack-500'}`}
                    >
                        <IconDownloadArrow className="w-[15px] h-[15px] flex-none rotate-180"/>
                        <span>{isDragging ? t('itineraryDropPdf') : t('itineraryPdfAttach')}</span>
                    </label>

                    <span className="text-xs text-neutralBlack-400">{t('itineraryOr')}</span>

                    <input
                        type="url"
                        value={urlDraft}
                        onChange={(e) => setUrlDraft(e.target.value)}
                        onBlur={commitUrl}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                commitUrl()
                            }
                        }}
                        placeholder={t('itineraryPasteLink')}
                        className="flex-1 basis-[150px] min-w-0 h-[38px] px-3.5 rounded-full border border-dashed border-neutralBlack-300 bg-transparent text-[13px] text-neutralBlack-900 placeholder:text-neutralBlack-400 outline-none transition-colors duration-200 ease-out focus:border-solid focus:border-neutralBlack-900"
                    />
                </div>
            )}
        </div>
    )
}
